import { isPlatformBrowser } from '@angular/common';
import { inject, makeStateKey, PLATFORM_ID, REQUEST, TransferState } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SOCIAL_AUTH_ERROR, SOCIAL_AUTH_TOKEN } from '../../../core/services/auth/auth.service';
import { AppleAuthService } from '../../../services/apple-auth/apple-auth.service';
import {
  GoogleAuthService,
  SocialAuthError,
} from '../../../services/google-auth/google-auth.service';

export const REDIRECT_URL = makeStateKey<{
  internalRedirectUrl?: { url?: string; queryParams?: { [key: string]: string } };
}>('REDIRECT_URL');

export const socialSigninGuard: CanActivateFn = () => {
  const platform = inject(PLATFORM_ID);
  const router = inject(Router);
  const transferState = inject(TransferState);

  const request = inject(REQUEST);

  if (isPlatformBrowser(platform)) {
    const redirectUrl = transferState.get(REDIRECT_URL, null);
    return redirectUrl ? redirectURlHandler(router, redirectUrl) : router.createUrlTree(['/']);
  }

  if (!request) {
    throw new Error('REQUEST not provided');
  }

  if (request.method.toLowerCase() === 'get') {
    return handleGoogleLogin(request, router);
  }

  if (request.method.toLowerCase() === 'post') {
    return handleAppleLogin(request, router);
  }

  return router.createUrlTree(['/']);
};

async function handleGoogleLogin(
  request: globalThis.Request,
  router: Router
): Promise<UrlTree | boolean> {
  const googleAuthService = inject(GoogleAuthService);
  const urlSearchParams = new URLSearchParams(request.url.split('?')[1] ?? '');
  const transferState = inject(TransferState);

  const code = urlSearchParams.get('code');
  const state = extractState(urlSearchParams.get('state'));

  if (!code || !state || state.type !== 'google') {
    return router.createUrlTree(['/']);
  }

  const result = await firstValueFrom(
    googleAuthService.processGoogleCode(code, {
      language: state.language,
      currency: state.currency,
    })
  );

  if (isSocialAuthError(result)) {
    console.error(new Error('Google Signin Failed: ' + result.error));
    transferState.set(SOCIAL_AUTH_ERROR, result.error);
    transferState.set(SOCIAL_AUTH_TOKEN, null);
  } else {
    transferState.set(SOCIAL_AUTH_TOKEN, result);
  }

  return utils.redirectURlHandler(router, state, transferState);
}

async function handleAppleLogin(
  request: globalThis.Request,
  router: Router
): Promise<UrlTree | true> {
  const appleAuthService = inject(AppleAuthService);
  const transferState = inject(TransferState);

  const { code, state: appleState } = await utils.parseUrlEncodedBody(request.body as never);

  const state = extractState(appleState);

  if (!code || !state || state.type !== 'apple') {
    return router.createUrlTree(['/']);
  }

  const result = await firstValueFrom(
    appleAuthService.processAppleCode(code, {
      language: state.language,
      currency: state.currency,
    })
  );

  if (isSocialAuthError(result)) {
    console.error(new Error('Apple Signin Failed: ' + result.error));
    transferState.set(SOCIAL_AUTH_ERROR, result.error);
    transferState.set(SOCIAL_AUTH_TOKEN, null);
  } else {
    transferState.set(SOCIAL_AUTH_TOKEN, result);
  }

  return utils.redirectURlHandler(router, state, transferState);
}

function extractState(state: string | undefined | null):
  | {
      type: 'google' | 'apple';
      internalRedirectUrl?: { url?: string; queryParams?: { [key: string]: string } };
      language?: string;
      currency?: string;
    }
  | '' {
  if (!state) {
    return '';
  }

  try {
    return JSON.parse(state);
  } catch {
    return '';
  }
}

function isSocialAuthError(result: unknown): result is SocialAuthError {
  return (
    typeof result === 'object' &&
    result !== null &&
    'success' in result &&
    (result as SocialAuthError).success === false
  );
}

const parseUrlEncodedBody = async (
  bodyStream: ReadableStream<Uint8Array>
): Promise<Record<string, string>> => {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let body = '';

  let done: boolean;
  let value: Uint8Array<ArrayBufferLike> | undefined = undefined;

  let result = await reader.read();
  done = result.done;
  value = result.value;

  while (!done) {
    body += decoder.decode(value, { stream: true });
    result = await reader.read();
    done = result.done;
    value = result.value;
  }
  body += decoder.decode(); // flush

  const parsed: Record<string, string> = {};

  // Split into key=value pairs
  for (const pair of body.split('&')) {
    if (!pair) continue;
    const [key, value] = pair.split('=');
    parsed[decodeURIComponent(key)] = decodeURIComponent(value || '');
  }

  return parsed;
};

const redirectURlHandler = (
  router: Router,
  state: {
    internalRedirectUrl?: { url?: string; queryParams?: { [key: string]: string } };
  },
  transferState?: TransferState | null
): true | UrlTree => {
  if (state.internalRedirectUrl?.url) {
    const route = router.createUrlTree([state.internalRedirectUrl.url], {
      queryParams: state.internalRedirectUrl.queryParams || {},
    });

    transferState?.set(REDIRECT_URL, state);
    return route;
  }

  return true;
};

export const utils = {
  parseUrlEncodedBody,
  redirectURlHandler,
};
